import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Plug, ShieldCheck } from "lucide-react";

import { Spinner } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { SCOPE_LABELS } from "../lib/scopes.js";

/**
 * The consent screen: an app (Grok, Claude, Cursor) asks to connect.
 *
 * It stands outside the shell, like the wall — somebody arrives here from
 * another app and leaves straight back to it. Read access is ticked; anything
 * that changes something starts unticked, and ticking it costs a passkey now,
 * whatever this browser proved earlier. Where the code goes is shown above the
 * name, because the address is what cannot be faked by registering.
 */
export function Connect() {
    const { id } = useParams();
    const [request, setRequest] = useState(null);
    const [error, setError] = useState(null);
    const [scopes, setScopes] = useState(["projects:read"]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.oauth.request(id).then(setRequest).catch((caught) => setError(caught.message));
    }, [id]);

    const decide = async (approve) => {
        setBusy(true);
        setError(null);
        try {
            const { redirect } = approve ? await api.oauth.approve(id, scopes) : await api.oauth.deny(id);
            window.location.assign(redirect);
        } catch (caught) {
            setError(caught.message);
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center px-4 py-8">
            <div className="card w-full max-w-md p-6">
                <div className="mb-4 flex items-center gap-2">
                    <Plug size={18} className="muted" />
                    <h1 className="text-lg font-semibold">Connect an app</h1>
                </div>

                {!request && !error ? <Spinner className="muted" /> : null}
                {error && !request ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

                {request ? (
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm">
                                <span className="font-medium">{request.client.name}</span> wants to operate scaling-ops.
                            </p>
                            <p className="mt-1 text-xs muted">
                                It will be answered at <code className="font-mono">{request.client.redirect}</code>
                                {request.client.metadataDocument ? " (verified by its own published registration)" : ""}.
                                Only continue if you just started this from that app.
                            </p>
                        </div>

                        <div className="space-y-2">
                            {request.scopes.map((scope) => {
                                const writes = request.writeScopes.includes(scope);
                                return (
                                    <label key={scope} className="flex items-start gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            className="mt-1"
                                            checked={scopes.includes(scope)}
                                            onChange={(event) =>
                                                setScopes((previous) => (event.target.checked ? [...previous, scope] : previous.filter((s) => s !== scope)))
                                            }
                                        />
                                        <span>
                                            {SCOPE_LABELS[scope] ?? scope}
                                            {writes ? <span className="block text-xs muted">Deletions still wait for your passkey.</span> : null}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>

                        <p className="flex items-start gap-2 text-xs muted">
                            <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                            Environment values can never be read through this connection, and it can be disconnected at any time
                            in Settings → Connected apps.
                        </p>

                        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

                        <div className="flex gap-2">
                            <button className="btn-ghost flex-1" disabled={busy} onClick={() => decide(false)}>Deny</button>
                            <button className="btn-primary flex-1" disabled={busy || scopes.length === 0} onClick={() => decide(true)}>
                                {busy ? <Spinner size={14} /> : null} Allow
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
